import { ssoUrl, externalLoginUrl } from "../constants/api";
import { SignInFromCookieType } from "../types/auth";
import { FetchOptions, FetchResponse } from "../types/fetch";

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempts to refresh the token using stored cookie-based login
 */
const refreshToken = async (): Promise<string | null> => {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const gambod = localStorage.getItem("gambod");
      const parsedGambod: { cookie?: string } | null = gambod ? JSON.parse(gambod) : null;
      const cookie = parsedGambod?.cookie;

      if (!cookie) return null;

      const body: SignInFromCookieType = { cookie };
      const response = await fetch(`${ssoUrl}loginFromCookie`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) return null;

      const data = await response.json();
      const responseData = data?.data || data;
      if (!responseData?.token) return null;

      const { token, cookie: newCookie, cookieName: newCookieName, user } = responseData;
      if (gambod) {
        const state = JSON.parse(gambod);
        Object.assign(state, { token, cookie: newCookie ?? cookie, cookieName: newCookieName });
        if (user) {
          state.user = {
            cookie: newCookie ?? cookie,
            display_name: user.DisplayName,
            email: user.Email,
            username: user.Username,
            permissions: user.Permissions,
          };
        }
        localStorage.setItem("gambod", JSON.stringify(state));
      }

      return token;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Enhanced fetch with automatic 401 handling and token refresh
 */
export const fetchWithAuth = async (url: RequestInfo | URL, options: FetchOptions = {}): Promise<Response> => {
  const { skipAuth = false, skip401Retry = false, ...fetchOptions } = options;

  // Prepare headers with auth token if needed
  const headers = new Headers(fetchOptions.headers);
  if (!skipAuth) {
    try {
      const gambod = localStorage.getItem("gambod");
      const state = gambod ? JSON.parse(gambod) : null;
      if (state?.token) {
        headers.set("Authorization", `Bearer ${state.token}`);
      }
    } catch (error) {
      console.warn("Failed to parse gambod from localStorage", error);
    }
  }

  // Auto-set Content-Type for JSON strings
  if (fetchOptions.body && !headers.has("Content-Type") && typeof fetchOptions.body === "string") {
    headers.set("Content-Type", "application/json");
  }

  // Make initial request
  let response = await fetch(url, { ...fetchOptions, headers });

  // Handle 401 with token refresh
  if (response.status === 401 && !skip401Retry && !skipAuth) {
    const newToken = await refreshToken();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      response = await fetch(url, { ...fetchOptions, headers });
    } else {
      localStorage.removeItem("gambod");
      window.location.href = externalLoginUrl;
    }
  }

  return response;
};

/**
 * Creates a standardized response object for consistency across API calls
 */
/**
 * Fetch wrapper that returns a standardized response structure with automatic JSON parsing
 */
export const fetchWithAuthJson = async <T = any>(url: RequestInfo | URL, options: FetchOptions = {}): Promise<FetchResponse<T>> => {
  const response = await fetchWithAuth(url, options);
  let data: T;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : (text as unknown as T);
  } catch {
    data = (await response.text()) as unknown as T;
  }

  return { data, status: response.status, statusText: response.statusText, headers: response.headers, ok: response.ok };
};
